import { z } from 'zod';
import { LifecycleStateSchema } from './lifecycle.js';
import { PageMetaSchema, PageQuerySchema, SortDirectionSchema } from './pagination.js';
export const ORGANISATION_TYPES = [
    'clinic',
    'hospital',
    'diagnostic_centre',
    'hospital_group',
];
export const OrganisationTypeSchema = z.enum(ORGANISATION_TYPES);
export const DEPLOYMENT_TYPES = ['cloud', 'on_prem'];
export const DeploymentTypeSchema = z.enum(DEPLOYMENT_TYPES);
/** ISO 3166-1 alpha-2 country code, optionally with a subdivision, e.g. IN or IN-KA. */
export const JurisdictionSchema = z
    .string()
    .regex(/^[A-Z]{2}(-[A-Z0-9]{1,3})?$/, 'jurisdiction must be ISO 3166 (e.g. IN or IN-KA)');
const NameSchema = z.string().trim().min(2).max(200);
/**
 * Organisation contact. Business contact data only — CONFIDENTIAL class.
 * No clinical or patient-adjacent fields are modelled or accepted.
 */
export const ContactInputSchema = z
    .object({
    fullName: z.string().trim().min(2).max(120),
    roleTitle: z.string().trim().min(2).max(120),
    email: z.email().max(254),
    phone: z
        .string()
        .trim()
        .regex(/^\+?[0-9 ()-]{7,20}$/)
        .optional(),
    isPrimary: z.boolean().default(false),
    /** Why this person is stored (Stage 2 purpose/consent metadata). */
    purpose: z
        .enum(['commercial', 'technical', 'billing', 'escalation', 'data_protection'])
        .default('commercial'),
    consentBasis: z.enum(['contract', 'legitimate_interest', 'consent']).default('contract'),
    consentNote: z.string().trim().max(500).optional(),
})
    .strict();
export const ContactSchema = ContactInputSchema.extend({
    id: z.uuid(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
export const TagSchema = z
    .string()
    .trim()
    .toLowerCase()
    .regex(/^[a-z0-9][a-z0-9-]{1,30}$/, 'tags are lowercase alphanumeric with hyphens');
export const OrganisationCreateSchema = z
    .object({
    legalName: NameSchema,
    displayName: NameSchema,
    organisationType: OrganisationTypeSchema,
    deploymentType: DeploymentTypeSchema,
    jurisdiction: JurisdictionSchema,
    contacts: z.array(ContactInputSchema).max(20).default([]),
    tags: z.array(TagSchema).max(20).default([]),
    ownerUserIds: z.array(z.uuid()).max(10).default([]),
})
    .strict()
    .refine((v) => v.contacts.filter((c) => c.isPrimary).length <= 1, {
    message: 'at most one contact may be primary',
    path: ['contacts'],
});
export const OrganisationUpdateSchema = z
    .object({
    legalName: NameSchema.optional(),
    displayName: NameSchema.optional(),
    organisationType: OrganisationTypeSchema.optional(),
    deploymentType: DeploymentTypeSchema.optional(),
    jurisdiction: JurisdictionSchema.optional(),
    contacts: z.array(ContactInputSchema).max(20).optional(),
    tags: z.array(TagSchema).max(20).optional(),
    ownerUserIds: z.array(z.uuid()).max(10).optional(),
    /** Optimistic-concurrency guard; must match the stored version. */
    expectedVersion: z.number().int().min(1),
})
    .strict()
    .refine((v) => (v.contacts ?? []).filter((c) => c.isPrimary).length <= 1, {
    message: 'at most one contact may be primary',
    path: ['contacts'],
});
export const OrganisationArchiveSchema = z
    .object({
    reason: z.string().trim().min(5).max(1000),
    expectedVersion: z.number().int().min(1),
})
    .strict();
export const OwnerSchema = z.object({
    userId: z.uuid(),
    displayName: z.string(),
    assignedAt: z.iso.datetime(),
});
export const OrganisationSchema = z.object({
    id: z.uuid(),
    legalName: z.string(),
    displayName: z.string(),
    organisationType: OrganisationTypeSchema,
    deploymentType: DeploymentTypeSchema,
    jurisdiction: z.string(),
    lifecycleState: LifecycleStateSchema,
    tags: z.array(z.string()),
    owners: z.array(OwnerSchema),
    contacts: z.array(ContactSchema),
    version: z.number().int(),
    archivedAt: z.iso.datetime().nullable(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
/** Compact row for the registry list (contacts elided for volume). */
export const OrganisationListItemSchema = OrganisationSchema.omit({ contacts: true }).extend({
    primaryContactName: z.string().nullable(),
    contactCount: z.number().int(),
});
export const ORGANISATION_SORT_FIELDS = [
    'displayName',
    'legalName',
    'lifecycleState',
    'jurisdiction',
    'updatedAt',
    'createdAt',
];
export const OrganisationListQuerySchema = PageQuerySchema.extend({
    search: z.string().trim().max(200).optional(),
    lifecycleState: LifecycleStateSchema.optional(),
    organisationType: OrganisationTypeSchema.optional(),
    deploymentType: DeploymentTypeSchema.optional(),
    jurisdiction: JurisdictionSchema.optional(),
    tag: TagSchema.optional(),
    ownerUserId: z.uuid().optional(),
    // stringbool: query strings arrive as text; plain coercion would read "false" as true
    includeArchived: z.stringbool().default(false),
    /** Attention filter used by Mission Overview drill-downs. */
    attention: z.enum(['stale', 'missing_contacts', 'unowned']).optional(),
    sortBy: z.enum(ORGANISATION_SORT_FIELDS).default('updatedAt'),
    sortDir: SortDirectionSchema.default('desc'),
});
export const OrganisationListResponseSchema = z.object({
    items: z.array(OrganisationListItemSchema),
    meta: PageMetaSchema,
});
//# sourceMappingURL=organisation.js.map